import type { RequirementViolationKind } from "../flow-requirements";
// Flow Types (shared between web and api apps)

import type { FlowPageType } from "../flow-component-policy";
import type { LocalizedString } from "../i18n";
import { clientTypeAllowed } from "./client-types";
import type { CloneJob } from "./clone-jobs";
import type {
  CapacityInputFrequency,
  FinancialFieldDefinition,
  FsClientType,
} from "./financial-situation-models";
import type { CancelReasonMode, ComplianceApprovalScope } from "./flow-instances";
import type { FlowFieldScope } from "./public-api-flow-fields";

// --- Flow ---

/**
 * QL-239 — a flow's lifecycle state. Inactive: nobody starts new instances;
 * existing ones can still be submitted. Preview: only designers may start and
 * submit, and only through the app. Active: everyone. Existing instances can
 * be opened in every state.
 */
export const FLOW_STATUSES = ["inactive", "preview", "active"] as const;
export type FlowStatus = (typeof FLOW_STATUSES)[number];

export type SetFlowStatusRequest = {
  status: FlowStatus;
};

export type SetFlowStatusResponse = { success: true };

export type Flow = {
  id: string;
  name: string;
  description: string | null;
  riskModelId: string | null;
  portfolioCollectionId: string | null;
  financialSituationModelId: string | null;
  currency: string;
  language: string;
  status: FlowStatus;
  multiGoal: boolean;
  /** QL-593 — goal cap per instance while multiGoal is on (1–30). */
  maxGoals: number;
  multiPortfolio: boolean;
  /**
   * QL-401 — hard opt-in gate for followup eligibility. True implies the flow
   * is active (enable requires active; leaving active forces it off).
   */
  supportedForFollowup: boolean;
  allowPublicStart: boolean;
  allowPublicPdfGen: boolean;
  allowPublicSigning: boolean;
  /** QP-184 — which client types may start this flow; null = all. */
  allowedClientTypes?: string[] | null;
  order: number;
  tenantId: string;
  createdById: string;
  updatedById: string;
  createdAt: string;
  updatedAt: string;
};

/**
 * QP-184 — flow-start client-type gate. Null/empty list = open to all types.
 * Shared by the API gates (createInstance, public start) and the web
 * startable-flows filter so the rule cannot drift.
 *
 * QL-36 moved the body to `clientTypeAllowed`; the goal-type gate calls the
 * same implementation through its own named wrapper.
 */
export function clientTypeAllowedForFlow(
  allowedClientTypes: string[] | null | undefined,
  clientType: string | null | undefined,
): boolean {
  return clientTypeAllowed(allowedClientTypes, clientType);
}

// --- Flow Field ---

/**
 * An option row as returned by the API (flow_field_option table, QL-201).
 * Options for dropdown/radio/multiselect flow fields are rows with a stable
 * id; stored answers and conditional rules reference the id, so the label
 * can be edited without breaking existing data. A flow is bound to one
 * language, so the label is a single string (no labelI18n).
 */
export type FlowFieldOption = {
  id: string;
  label: string;
  order: number;
  isArchived: boolean;
  /**
   * Identifier an integration uses for this option instead of its id; unique
   * among the field's active options. Only settable while the field itself
   * has a sync key.
   */
  syncKey: string | null;
};

/**
 * An option as submitted when creating/updating a flow field. Existing
 * options carry their id (label edits keep the id stable); new options omit
 * it. Options absent from the submitted list are archived, never deleted.
 */
export type FlowFieldOptionInput = {
  id?: string;
  label: string;
  /** Omitted or empty clears the key. */
  syncKey?: string | null;
};

export type FlowField = {
  id: string;
  flowId: string;
  name: string;
  type: string;
  options: FlowFieldOption[];
  isRequired: boolean;
  scope: FlowFieldScope;
  /** Identifier an integration uses for this field; unique per flow (QL-422). */
  syncKey: string | null;
  order: number;
  tenantId: string;
  createdById: string;
  updatedById: string;
  createdAt: string;
  updatedAt: string;
};

export type FlowFieldUsage = {
  count: number;
  pages: Array<{
    pageId: string;
    pageName: string;
    visibility: string;
    componentId: string;
  }>;
};

// --- Flow Page ---

export type FlowPageVisibility = "internal" | "public";

/** Where a global page renders: once before every goal, or once after them. */
export type GlobalPosition = "before_goals" | "after_goals";

/**
 * QL-117 — where a goal page renders inside its goal: before the goal's portfolio pages
 * (the historical position) or after them. Only meaningful for `pageType = "goal"`.
 */
export type GoalPosition = "before_portfolios" | "after_portfolios";

export const GLOBAL_POSITIONS = [
  "before_goals",
  "after_goals",
] as const satisfies readonly GlobalPosition[];
export const GOAL_POSITIONS = [
  "before_portfolios",
  "after_portfolios",
] as const satisfies readonly GoalPosition[];

export type FlowPage = {
  id: string;
  flowId: string;
  name: string;
  pageType: FlowPageType;
  visibility: FlowPageVisibility;
  globalPosition: GlobalPosition;
  goalPosition: GoalPosition;
  enableAdvisorNotes: boolean;
  /** QL-43 — a non-empty advisor note is a submit requirement on this page. */
  requireAdvisorNotes: boolean;
  conditionalVisibility: unknown;
  order: number;
  createdById: string;
  updatedById: string;
  createdAt: string;
  updatedAt: string;
};

// --- Flow Page Requests ---

export type CreateFlowPageRequest = {
  name: string;
  /** Defaults to "global". Fixed for the page's lifetime. */
  pageType?: FlowPageType;
  /** Defaults to "internal". */
  visibility?: FlowPageVisibility;
  /** Global pages only; ignored (normalized to the default) for other page types. */
  globalPosition?: GlobalPosition;
  /** QL-117 — goal pages only; ignored (normalized to the default) for other page types. */
  goalPosition?: GoalPosition;
};

/**
 * Editable page settings. `visibility`, `pageType` and the positions are NOT here: they fix
 * which section a page lives in, and a section change goes through the move endpoint (goal /
 * global position) or is impossible (type, visibility) once the page exists.
 */
export type UpdateFlowPageRequest = {
  name?: string;
  conditionalVisibility?: unknown;
  enableAdvisorNotes?: boolean;
  requireAdvisorNotes?: boolean;
};

/**
 * Move a page to another section of its own page type and renumber both the source and the
 * target section in one transaction. A global page moves between the before/after-goals
 * sections (`globalPosition`); a goal page moves between the before/after-portfolios sections
 * (`goalPosition`). Portfolio pages have a single section and cannot be moved.
 */
export type MoveFlowPageRequest = {
  globalPosition?: GlobalPosition;
  goalPosition?: GoalPosition;
  /** Index inside the target section; clamped to the section's length. */
  order: number;
};

/** Full ordered id list of ONE section (visibility + page type + position). */
export type ReorderFlowPagesRequest = {
  pageIds: string[];
};

export type FlowPageMutationResponse = { success: true };

// --- Create Flow From Template Request ---

export type CreateFlowFromTemplateComponent = {
  type: string;
  config?: unknown;
  columnPosition?: string | null;
  tabId?: string | null;
};

export type CreateFlowFromTemplateLayout = {
  layoutType: string;
  config?: unknown;
  components: CreateFlowFromTemplateComponent[];
};

export type CreateFlowFromTemplatePage = {
  name: string;
  visibility: FlowPageVisibility;
  pageType: FlowPageType;
  globalPosition?: GlobalPosition;
  /** QL-117 — goal pages only. */
  goalPosition?: GoalPosition;
  enableAdvisorNotes?: boolean;
  requireAdvisorNotes?: boolean;
  layouts?: CreateFlowFromTemplateLayout[];
  components?: CreateFlowFromTemplateComponent[];
};

export type CreateFlowFromTemplateRequest = {
  name: string;
  description?: string;
  language?: string;
  riskModelId?: string;
  portfolioCollectionId?: string;
  financialSituationModelId?: string;
  multiGoal?: boolean;
  /** QL-593 — goal cap per instance while multiGoal is on; omitted = the default (3). */
  maxGoals?: number;
  multiPortfolio?: boolean;
  allowPublicStart?: boolean;
  allowPublicPdfGen?: boolean;
  allowPublicSigning?: boolean;
  /**
   * QL-372 — three-letter upper-case ISO code; omitted = the linked
   * collection's currency, or NOK without one. Fixed after creation.
   */
  currency?: string;
  pages: CreateFlowFromTemplatePage[];
};

// --- Create / Update Flow Requests ---

/**
 * POST /internal/flows. `currency` omitted = the linked collection's currency,
 * or NOK without one; fixed after creation (QL-372).
 */
export type CreateFlowRequest = {
  name: string;
  description?: string;
  riskModelId?: string;
  portfolioCollectionId?: string;
  /** Three-letter upper-case ISO code. */
  currency?: string;
  language?: string;
};

/** PATCH /internal/flows/:id. The currency is fixed at creation; a sent value is ignored (QL-372). */
export type UpdateFlowRequest = {
  name?: string;
  description?: string | null;
  riskModelId?: string | null;
  portfolioCollectionId?: string | null;
};

/** PATCH /internal/flows/:id/settings. The currency is fixed at creation; a sent value is ignored (QL-372). */
export type UpdateFlowSettingsRequest = {
  name?: string;
  description?: string | null;
  riskModelId?: string | null;
  portfolioCollectionId?: string | null;
  financialSituationModelId?: string | null;
  allowPublicStart?: boolean;
  allowPublicPdfGen?: boolean;
  allowPublicSigning?: boolean;
  multiGoal?: boolean;
  /** QL-593 — goal cap per instance of a multi-goal flow. */
  maxGoals?: number;
  multiPortfolio?: boolean;
  requireComplianceApproval?: boolean;
  complianceApprovalScope?: ComplianceApprovalScope;
  requireExternalApproval?: boolean;
  enableEidSigning?: boolean;
  enableDigitalSigning?: boolean;
  /** Render the PDF and advance the process automatically when an instance is submitted. */
  autoGeneratePdfOnSubmit?: boolean;
  cancelReasonMode?: CancelReasonMode;
  /** QP-184 — which client types may start this flow; null = all. */
  allowedClientTypes?: string[] | null;
};

// --- Clone Flow Request ---

export type CloneFlowRequest = {
  name: string;
  description?: string;
  /**
   * QL-334 — the clone's language (a tenant-supported locale). Absent = the
   * source flow's language.
   */
  language?: string;
  /**
   * QL-334 — when `language` differs from the source, AI-translate the clone's
   * texts (page names, field names and options, component texts, and the PDF
   * template's texts when it is cloned too). Ignored for the same language.
   */
  translate?: boolean;
  /**
   * QL-334 — also clone the flow's PDF template onto the new flow, with its
   * field, option, quiz and page references pointed at the clone.
   */
  includePdfTemplate?: boolean;
  /**
   * QL-334 — name of the PDF template copy when `includePdfTemplate` is set.
   * Absent or blank = "<source template name> (copy)".
   */
  pdfTemplateName?: string;
};

/**
 * QL-334 — POST /internal/flows/:id/clone. A plain copy is made at once
 * ("copied", 201). A translated copy takes up to a minute, so it is queued
 * ("queued", 202) and the job reports its progress.
 */
export type CloneFlowResponse = { kind: "copied"; id: string } | { kind: "queued"; job: CloneJob };

// --- Duplicate Flow Field Request ---

/**
 * POST /internal/flows/:id/fields/:fieldId/duplicate (QL-234). A field's scope
 * is fixed once it exists, so a designer who picked the wrong one duplicates
 * the field into the scope they need. Responds with the new `FlowField`.
 */
export type DuplicateFlowFieldRequest = {
  name: string;
  scope: FlowFieldScope;
};

// --- Flow Page Component ---

export type FlowPageComponent = {
  id: string;
  flowPageId: string;
  flowPageLayoutId: string | null;
  componentType: string;
  flowFieldId: string | null;
  config: string | null;
  columnPosition: string | null;
  tabId: string | null;
  order: number;
  createdById: string;
  updatedById: string;
  createdAt: string;
  updatedAt: string;
  flowField?: {
    id: string;
    name: string;
    type: string;
    isRequired: boolean;
  } | null;
};

// --- Flow Page Layout ---

export type FlowPageLayout = {
  id: string;
  flowPageId: string;
  layoutType: string;
  config: string | null;
  order: number;
  createdById: string;
  updatedById: string;
  createdAt: string;
  updatedAt: string;
};

// --- AI Improve Text ---

export type AiImproveTextResult = {
  improvedText: string;
  stats: {
    timing: number;
    tokens: { request: number; response: number; total: number };
    cost: number;
  };
};

// --- Public API: goal types of a flow (QL-301) ---
// `GET /api/v1/flows/{id}/goal-types` — the goal types of the flow's portfolio
// collection, in picker order. `id` is what the goals endpoints accept as
// `goalType`. Slimmer than `PortfolioCollectionGoalType`: no tenant or audit
// columns, and no collection id — the item is always read through its flow,
// whose collection is `PublicApiFlow.portfolioCollectionId` (QL-523).

export type FlowGoalTypeItem = {
  id: string;
  labelI18n: LocalizedString;
  iconSvg: string | null;
  /** Client types that may pick this goal type; null = all. */
  allowedClientTypes: string[] | null;
  order: number;
};

/**
 * Empty when the flow has no collection or the collection defines no goal
 * types — the goals endpoints then accept any `goalType` value.
 */
export type FlowGoalTypesResponse = {
  data: FlowGoalTypeItem[];
};

// --- Public API: financial situation fields of a flow (QL-548) ---
// `GET /api/v1/flows/{id}/financial-situation/fields` — the fields of the
// flow's financial situation model, readable without a flow instance. The
// fields of the instance endpoint, each with its `clientTypes` always set, so
// an unfiltered list says which types each field applies to.

export type FlowFinancialSituationField = Omit<FinancialFieldDefinition, "clientTypes"> & {
  /** The types `fieldAppliesTo` accepts the field for; never empty (untagged = Person). */
  clientTypes: FsClientType[];
};

export type FlowFinancialSituationFieldsResponse = {
  /** The flow currency; every entry amount must be in it. */
  currency: string;
  /** Echoed when the list was narrowed with `?clientType=`; absent otherwise. */
  clientType?: FsClientType;
  /**
   * QL-752 — per client type, the triangle input frequency the save accepts;
   * null where the capacity calculator is off for that type.
   */
  capacityInputFrequency: Record<FsClientType, CapacityInputFrequency | null>;
  incomeFields: FlowFinancialSituationField[];
  assetFields: FlowFinancialSituationField[];
  liabilityFields: FlowFinancialSituationField[];
  commitmentFields: FlowFinancialSituationField[];
};

// --- Public API: flow resource ---
//
// GET /api/v1/flows and GET /api/v1/flows/{id}.

export type PublicApiFlow = {
  id: string;
  name: string;
  description: string | null;
  /** QL-239 — inactive / preview / active. */
  status: FlowStatus;
  /** @deprecated Use `status`. True exactly when `status` is "active". */
  isActive: boolean;
  /** QL-401 — whether followups may be created on this flow (hard opt-in gate). */
  supportedForFollowup: boolean;
  cancelReasonMode: CancelReasonMode;
  /** ISO 4217 calculation currency. */
  currency: string;
  /** Locale code the flow runs in (e.g. `en_GB`). */
  language: string;
  /** QP-184 — client types that may start this flow; null = all. */
  allowedClientTypes: string[] | null;
  /** QL-523 — the collection the flow's portfolios and products come from; null = none. */
  portfolioCollectionId: string | null;
  /** QL-523 — the financial situation model the flow uses; null = none. */
  financialSituationModelId: string | null;
  /** QL-523 — the risk model the flow scores and checks compliance with; null = none. */
  riskModelId: string | null;
  multiGoal: boolean;
  /** QL-593 — goal cap per instance; only applies while multiGoal is true. */
  maxGoals: number;
  multiPortfolio: boolean;
  /** QL-523 — process settings as currently configured on the flow. */
  requireComplianceApproval: boolean;
  complianceApprovalScope: ComplianceApprovalScope;
  requireExternalApproval: boolean;
  enableEidSigning: boolean;
  enableDigitalSigning: boolean;
  autoGeneratePdfOnSubmit: boolean;
  /** QL-523 — client-facing (public link) flow settings. */
  allowPublicStart: boolean;
  allowPublicPdfGen: boolean;
  allowPublicSigning: boolean;
  createdAt: string;
  updatedAt: string;
};

export type PublicApiFlowListResponse = {
  data: PublicApiFlow[];
  meta: { total: number; limit: number; offset: number };
};

/** A component placed on a flow page, named so a designer can find it. */
export type FlowComponentPlacement = {
  componentId: string;
  componentType: string;
  pageId: string;
  pageName: string;
};

/**
 * A requirement the flow's present components need but its assignments don't meet, with
 * every placement responsible for it. Returned with the flow and page detail so the editor
 * can warn before Activate refuses the flow (PR 546: existing flows that place
 * product_assessment or portfolio_list without a collection).
 */
export type FlowRequirementIssue = {
  kind: RequirementViolationKind;
  components: FlowComponentPlacement[];
};
